/**
 * Authored Drawing Robot Journey pages.
 *
 * This is intentionally data, not page-specific React code. The same block
 * renderer can later consume blocks stored in the Journey manifest/database.
 */
const DRAWING_ROBOT_STEP_PAGES = {
  "mysterious-artwork": {
    step_id: "mysterious-artwork",
    title: "The Mysterious Artwork",
    subtitle: "Study the Evidence and Form Your First Theory",
    phase: "Mystery",
    story:
      "Study the evidence, notice the clues, and form your first theory before the next clue is revealed.",
    blocks: [
      {
        id: "robot-artist-story-prelude",
        type: "story_prelude",
        eyebrow: "BEFORE THE ART-ROOM INVESTIGATION",
        title: "Enter the Mystery of the Robot Artist",
        prompt:
          "Watch what happened in the art room, then read Case File 01 before the artist reveal.",
        video: {
          title: "The Mystery of the Robot Artist",
          path: "drawing-robot/prelude/robot_artist_story_v1.mp4",
          poster: "drawing-robot/prelude/robot_artist_story_poster.jpg",
          caption:
            "This story introduces the unexplained artwork. It does not reveal who—or what—made the drawings.",
        },
        reading: {
          tab_label: "Read Case File 01",
          eyebrow: "SARAS-3D SUMMER CAMP — DAY 4",
          title: "Case File 01: The Mysterious Artwork",
          content: [
            {
              type: "paragraph",
              text:
                "Case File 01 was opened at 7:45 this morning, one day before the Young Makers Gallery.",
            },
            {
              type: "paragraph",
              text:
                "Studio Coordinator Mira expected to find four blank sheets of paper on the central art-room table. Instead, each sheet held a finished drawing. No artist’s name appeared on the pages. No label had been filled in. No one was waiting to claim the work.",
            },
            {
              type: "paragraph",
              text:
                "Mira placed clear covers over the drawings so that nothing would be smudged or moved. She photographed each page from above, noted that none carried a name or title, and began an evidence log before anyone offered an explanation.",
            },
            {
              type: "paragraph",
              text:
                "Several pictures contain loops, spirals, tangled lines, and patterns that repeat again and again. Some lines look smooth, while others shake or wander. In a few places, lines cross many times. Some marks seem to continue for a long distance without stopping. One artwork looks noticeably different from the others, although people do not yet agree about which detail matters most.",
            },
            {
              type: "callout",
              title: "Observation before explanation",
              text:
                "These are observations. They describe what can be seen. They do not yet explain who, or what, made the pictures.",
            },
            {
              type: "paragraph",
              text:
                "By midmorning, theories were spreading through the hallway. One person thought a student had returned after the room was closed. Another guessed that several artists had worked together. Someone wondered whether an object had moved across the paper. Others suggested that shaking, rolling, or an accident might have produced some of the patterns.",
            },
            {
              type: "paragraph",
              text:
                "Every idea sounded possible for a moment. None of them was supported strongly enough to close the case.",
            },
            {
              type: "paragraph",
              text:
                "Mira has asked investigators to pay special attention to evidence inside the artwork itself: repeated shapes, places where lines cross, long continuous-looking marks, recognizable objects, carefully planned areas, and patterns that may have happened accidentally.",
            },
            {
              type: "paragraph",
              text:
                "The case does not require everyone to choose the same artwork as the odd one out. Two investigators may make different choices and both reason well, as long as each points to visible evidence. A strong explanation says more than, ‘It looks weird.’ It identifies a detail and explains why that detail matters.",
            },
            {
              type: "paragraph",
              text:
                "Your team has now received copies of all four artworks. Do not search for a final answer yet. Begin by looking slowly. Record exactly what you notice before naming a cause. Compare the drawings. Choose the one that seems most different. Then form an opening theory and connect it to at least one clue.",
            },
            {
              type: "paragraph",
              text:
                "Your theory may change when the next piece of evidence is revealed. That is not a failure. Investigators improve ideas when new evidence appears.",
            },
            {
              type: "callout",
              title: "Investigator Note",
              text:
                "A strange picture is not a conclusion. First describe the marks. Then ask what could have caused them. — Studio Coordinator Mira",
            },
            {
              type: "numbered_list",
              title: "CASE FILE 01 — INVESTIGATION RULES",
              emphasize: true,
              items: [
                "RECORD AT LEAST THREE VISIBLE CLUES.",
                "COMPARE REPEATED, SMOOTH, AND SHAKY LINES.",
                "CHOOSE THE ARTWORK THAT SEEMS MOST DIFFERENT.",
                "FORM A THEORY AND BE READY TO REVISE IT.",
              ],
            },
            {
              type: "question",
              text:
                "What do the lines and patterns reveal—and which theory best fits the evidence you can see right now?",
            },
          ],
        },
      },
    ],
  },
  "accept-the-challenge": {
    step_id: "accept-the-challenge",
    title: "The Case Breaks Open",
    subtitle: "Accept the Robot Artist Mission",
    phase: "Mission",
    story:
      "A new piece of evidence reveals that the mysterious artist is a small machine. Your job is no longer only to solve the mystery—you have been recruited to rebuild and improve the artist.",
    blocks: [
      {
        id: "case-update",
        type: "story_hook",
        eyebrow: "CASE UPDATE",
        title: "The artist has been found",
        body:
          "Before the next clue is revealed, look back at your original theory. What did you think was creating the looping marks?",
        tone: "mystery",
      },
      {
        id: "robot-reveal",
        type: "image_reveal",
        title: "Reveal the mystery artist",
        prompt: "Tap when you are ready for the next piece of evidence.",
        image: {
          path: "drawing-robot/step-2/drawing_robot_reveal.png",
          alt:
            "A small drawing robot standing on marker legs above colorful looping artwork.",
        },
        save_as: "reveal_viewed",
      },
      {
        id: "theory-comparison",
        type: "choice_cards",
        title: "How did the new evidence compare with your theory?",
        prompt: "Choose the statement that best matches your thinking.",
        choices: [
          { id: "close", label: "My theory was close" },
          { id: "different", label: "I had a different idea" },
          { id: "questions", label: "I still need more evidence" },
        ],
        save_as: "theory_comparison",
        required: true,
      },
      {
        id: "clue-now-makes-sense",
        type: "evidence_response",
        title: "Reopen the case file",
        prompt:
          "What clue from the artwork makes more sense now that you have seen the robot?",
        placeholder: "For example: The repeated loops might come from...",
        save_as: "revealed_clue_evidence",
        min_length: 15,
        required: true,
      },
      {
        id: "robot-abilities",
        type: "multi_select",
        title: "What must a robot artist be able to do?",
        prompt: "Select every ability you think the system needs.",
        choices: [
          { id: "make_marks", label: "Make marks on paper" },
          { id: "receive_energy", label: "Receive stored energy" },
          { id: "convert_energy", label: "Turn energy into movement" },
          { id: "stay_together", label: "Stay assembled while moving" },
          { id: "stay_upright", label: "Stay upright enough to draw" },
          {
            id: "move_without_push",
            label: "Move without being pushed by hand",
          },
          { id: "choose_color", label: "Choose its own favorite color" },
        ],
        save_as: "robot_abilities",
        min_selections: 3,
        required: true,
      },
      {
        id: "criteria-constraints",
        type: "classification",
        title: "Engineer the mission",
        prompt:
          "Classify each statement as a success criterion or a constraint.",
        categories: [
          { id: "criterion", label: "Success criterion" },
          { id: "constraint", label: "Constraint" },
        ],
        items: [
          {
            id: "create_marks",
            label: "The robot must create visible marks on paper",
            correct_category: "criterion",
          },
          {
            id: "electrical_power",
            label: "The robot must move using electrical power",
            correct_category: "criterion",
          },
          {
            id: "remain_assembled",
            label: "The robot should remain together during testing",
            correct_category: "criterion",
          },
          {
            id: "supplied_parts",
            label: "Use only the supplied materials",
            correct_category: "constraint",
          },
          {
            id: "time_limit",
            label: "Complete the build during the available session",
            correct_category: "constraint",
          },
          {
            id: "safety",
            label: "Follow electrical and rotating-part safety rules",
            correct_category: "constraint",
          },
        ],
        save_as: "criteria_constraints",
        required: true,
      },
      {
        id: "mission-goal",
        type: "choice_cards",
        title: "Choose your first engineering goal",
        prompt: "You may revise this after testing.",
        choices: [
          { id: "wide_loops", label: "Create wide wandering loops" },
          {
            id: "repeating_patterns",
            label: "Create controlled repeating patterns",
          },
          { id: "stable_robot", label: "Build the most stable robot artist" },
          { id: "investigate_first", label: "Investigate first, then choose" },
        ],
        save_as: "mission_goal",
        required: true,
      },
      {
        id: "engineering-video",
        type: "video_asset",
        title: "How can a machine move without wheels?",
        mission:
          "Before watching, predict which parts must work together to create movement.",
        asset: {
          id: "drawing-robot-ignite-engineering",
          type: "saras_video",
          asset_ref: {
            source_video_id: 7031,
            tag_id: 30048,
          },
          provenance: {
            source_type: "collection",
            collection_id: 159,
            collection_item_id: 136,
          },
          content_context: {
            base_board: "saras",
            language_id: 1,
            board_topic_id: 933,
            source_topic_id: 959,
          },
        },
      },
      {
        id: "meet-robot-pwo",
        type: "pwo_asset",
        title: "Meet the virtual Drawing Robot",
        mission:
          "Explore the model. Notice what you can identify, but do not begin assembly yet.",
        asset: {
          id: "drawing-robot-mainmenu",
          type: "saras_pwo",
          asset_ref: {
            source_pwo_id: 1168,
            scene_name: "mainmenu",
          },
          provenance: {
            source_type: "collection",
            collection_id: 159,
            collection_item_id: 136,
          },
          content_context: {
            base_board: "saras",
            language_id: 1,
            board_topic_id: 933,
            source_topic_id: 959,
          },
        },
      },
      {
        id: "mission-saras",
        type: "saras_checkpoint",
        title: "Talk with Saras",
        suggested_questions: [
          "Which parts of this robot should I investigate first?",
          "How is a success criterion different from a constraint?",
          "What evidence from the artwork suggests the robot was moving?",
          "Help me turn my mission goal into something I can test.",
        ],
        hidden_context:
          "The student has completed Step 1 of the Drawing Robot Journey and has now learned that a small machine created the looping artwork. Help the student connect the reveal to prior observations, define an engineering mission, and distinguish criteria from constraints. Do not deliver the full science lesson about batteries, circuits, motors, off-center mass, vibration, balance, or friction yet. Ask one useful question at a time and preserve the student's ownership of the mission.",
        reveal_policy:
          "The robot may now be discussed. The deeper science should remain an investigation for later steps.",
      },
      {
        id: "step2-completion",
        type: "completion_gate",
        rules: [
          { field: "reveal_viewed", condition: "truthy" },
          { field: "theory_comparison", condition: "answered" },
          {
            field: "revealed_clue_evidence",
            condition: "min_length",
            value: 15,
          },
          { field: "robot_abilities", condition: "min_items", value: 3 },
          { field: "criteria_constraints", condition: "completed" },
          { field: "mission_goal", condition: "answered" },
        ],
      },
    ],
  },

  "investigate-the-kit": {
    step_id: "investigate-the-kit",
    title: "The Parts Are Clues",
    subtitle: "Kit Detective",
    phase: "Investigation",
    story:
      "The engineering team has received a virtual parts tray and a real kit. These pieces are not instructions yet—they are clues. Your job is to determine how the parts might cooperate as one system.",
    blocks: [
      {
        id: "step3-story",
        type: "story_hook",
        eyebrow: "EVIDENCE LOCKER",
        title: "A machine is more than a pile of parts",
        body:
          "A successful robot artist needs power, a path for electricity, a way to create motion, a stable structure, and something that can leave marks. Investigate before you build.",
        tone: "mystery",
      },
      {
        id: "accessories-pwo",
        type: "pwo_asset",
        title: "Explore the virtual evidence tray",
        mission:
          "Open the accessories scene. Click the parts, learn their names, and notice which pieces seem to belong together. Do not begin assembly yet.",
        save_as: "accessories_pwo_opened",
        asset: {
          id: "drawing-robot-accessories",
          type: "saras_pwo",
          asset_ref: {
            source_pwo_id: 1168,
            scene_name: "accessories",
          },
          provenance: {
            source_type: "collection",
            collection_id: 159,
            collection_item_id: 136,
          },
          content_context: {
            base_board: "saras",
            language_id: 1,
            board_topic_id: 933,
            source_topic_id: 959,
          },
        },
      },
      {
        id: "kit-evidence-gallery",
        type: "image_gallery",
        title: "Compare three views of the evidence",
        prompt:
          "Tap each image. Compare the clean virtual tray, the real unpacked kit, and the printed product-and-safety guide.",
        save_as: "inspected_image_id",
        images: [
          {
            id: "virtual-tray",
            label: "Virtual accessories tray",
            path:
              "drawing-robot/step-3/drawing_robot_accessories_virtual.png",
            caption:
              "The PWO presents the parts in an organized virtual tray so you can inspect one component at a time.",
            alt:
              "Virtual Drawing Robot accessories arranged on a wooden tray.",
          },
          {
            id: "real-kit",
            label: "Real unpacked kit",
            path:
              "drawing-robot/step-3/drawing_robot_parts_real.jpg",
            caption:
              "The real kit may look less organized. Use shape, material, wires, and connection points as evidence.",
            alt:
              "Drawing Robot kit parts spread across a wooden floor.",
          },
          {
            id: "product-guide",
            label: "Product and safety guide",
            path:
              "drawing-robot/step-3/drawing_robot_parts_guide.jpg",
            caption:
              "The printed guide shows the product list and an important electrical safety warning.",
            alt:
              "Printed Drawing Robot product list and electrical safety warnings.",
          },
        ],
      },
      {
        id: "real-kit-check",
        type: "multi_select",
        title: "Inventory your real kit",
        prompt:
          "Check each item only after you find it. Your kit may group small fasteners together.",
        choices: [
          { id: "base_plate", label: "Round base plate or robot body" },
          { id: "battery_holder", label: "Battery holder and battery" },
          { id: "switch", label: "On/off switch" },
          { id: "motor", label: "Small electric motor" },
          {
            id: "off_center_weight",
            label: "Small motor weight or uneven coupler",
          },
          {
            id: "metal_supports",
            label: "Metal marker supports or brackets",
          },
          { id: "markers", label: "Three colored markers" },
          {
            id: "wires",
            label: "Red/black wires and connecting pieces",
          },
          {
            id: "fasteners",
            label: "Screws, clips, rubber bands, or fasteners",
          },
          {
            id: "decorations",
            label: "Decorative eyes or optional body cover",
          },
        ],
        save_as: "parts_found",
        min_selections: 7,
        required: true,
      },
      {
        id: "part-systems",
        type: "classification",
        title: "Sort the parts into robot systems",
        prompt:
          "Choose the system that best describes each part's main role. Some parts cooperate with more than one system, but select the strongest match for now.",
        categories: [
          { id: "power", label: "Power" },
          {
            id: "electrical_path",
            label: "Electrical path and control",
          },
          { id: "motion", label: "Motion" },
          {
            id: "structure",
            label: "Structure and balance",
          },
          { id: "art", label: "Art-making" },
        ],
        items: [
          { id: "battery", label: "Battery and battery holder" },
          { id: "switch_wires", label: "Switch and wires" },
          { id: "motor", label: "Electric motor" },
          {
            id: "weight",
            label: "Small off-center motor weight",
          },
          {
            id: "base",
            label: "Base plate and support pieces",
          },
          {
            id: "marker_supports",
            label: "Marker supports or brackets",
          },
          { id: "markers", label: "Colored markers" },
        ],
        save_as: "part_systems",
        required: true,
      },
      {
        id: "part-jobs",
        type: "matching",
        title: "Predict the job of each major part",
        prompt:
          "These are engineering predictions. Later science investigations will let you test and revise them.",
        save_as: "part_jobs",
        items: [
          {
            id: "battery",
            label: "Battery",
            options: [
              {
                id: "stores_energy",
                label: "Stores energy that can be supplied to the circuit",
              },
              {
                id: "holds_marker",
                label: "Holds a marker against the paper",
              },
              {
                id: "creates_wobble",
                label: "Directly creates the robot's wobble",
              },
            ],
          },
          {
            id: "switch",
            label: "Switch",
            options: [
              {
                id: "opens_path",
                label: "Opens or closes the electrical path",
              },
              {
                id: "stores_energy",
                label: "Stores the robot's energy",
              },
              {
                id: "supports_body",
                label: "Supports the weight of the robot",
              },
            ],
          },
          {
            id: "wires",
            label: "Wires",
            options: [
              {
                id: "connect_path",
                label: "Connect electrical parts into a path",
              },
              {
                id: "make_marks",
                label: "Create visible marks on the paper",
              },
              {
                id: "balance_robot",
                label: "Act as the robot's balancing legs",
              },
            ],
          },
          {
            id: "motor",
            label: "Motor",
            options: [
              {
                id: "electric_to_rotation",
                label: "Changes electrical energy into rotation",
              },
              {
                id: "stores_energy",
                label: "Stores chemical energy for later",
              },
              {
                id: "controls_color",
                label: "Chooses which marker color to use",
              },
            ],
          },
          {
            id: "weight",
            label: "Off-center motor weight",
            options: [
              {
                id: "uneven_pull",
                label: "Makes the spinning pull unevenly and create vibration",
              },
              {
                id: "perfect_balance",
                label: "Makes the motor perfectly balanced",
              },
              {
                id: "complete_circuit",
                label: "Completes the electrical circuit",
              },
            ],
          },
          {
            id: "base",
            label: "Base plate",
            options: [
              {
                id: "support_positions",
                label: "Supports and positions the robot's components",
              },
              {
                id: "produce_power",
                label: "Produces electrical power",
              },
              {
                id: "open_circuit",
                label: "Opens and closes the circuit",
              },
            ],
          },
          {
            id: "marker_supports",
            label: "Marker supports",
            options: [
              {
                id: "hold_legs",
                label: "Hold the marker legs in chosen positions",
              },
              {
                id: "store_energy",
                label: "Store energy for the motor",
              },
              {
                id: "create_current",
                label: "Create electric current",
              },
            ],
          },
          {
            id: "markers",
            label: "Markers",
            options: [
              {
                id: "leave_evidence",
                label: "Touch the paper and leave visible evidence of motion",
              },
              {
                id: "power_motor",
                label: "Provide power to the motor",
              },
              {
                id: "control_switch",
                label: "Control whether the switch is open or closed",
              },
            ],
          },
        ],
        required: true,
      },
      {
        id: "mystery-weight-hypothesis",
        type: "choice_cards",
        title: "The mystery part",
        prompt:
          "Why might an engineer intentionally attach a weight that is not centered? Commit to your current hypothesis before the later motion investigation.",
        choices: [
          {
            id: "create_vibration",
            label:
              "It may turn smooth rotation into uneven pulling or vibration",
          },
          {
            id: "improve_balance",
            label: "It may make the spinning motor perfectly balanced",
          },
          {
            id: "store_energy",
            label: "It may store extra electrical energy",
          },
          {
            id: "decoration",
            label: "It may be included only for decoration",
          },
          {
            id: "not_sure",
            label: "I am not sure yet—but I have a question",
          },
        ],
        save_as: "mystery_weight_hypothesis",
        required: true,
      },
      {
        id: "mystery-weight-reason",
        type: "evidence_response",
        title: "Defend your hypothesis",
        prompt:
          "What detail about the part's shape, position, or connection supports your current idea?",
        placeholder:
          "I think this because the weight appears...",
        save_as: "mystery_weight_reason",
        min_length: 15,
        required: true,
      },
      {
        id: "student-kit-question",
        type: "evidence_response",
        title: "Write your own investigation question",
        prompt:
          "Before opening the build instructions, record one question you want Saras to help you investigate.",
        placeholder:
          "Why does the robot need...?",
        save_as: "student_kit_question",
        min_length: 8,
        required: true,
      },
      {
        id: "kit-saras",
        type: "saras_checkpoint",
        title: "Consult Saras without giving away the mystery",
        suggested_questions: [
          "How can I tell which parts belong to the electrical system?",
          "What evidence helps identify the motor and the switch?",
          "Why does the robot need both motion parts and structure parts?",
          "Can you help me improve my question about the off-center weight?",
        ],
        hidden_context:
          "The student is investigating the parts of a Drawing Robot before assembly. Use the student's parts_found, part_systems, part_jobs, mystery_weight_hypothesis, mystery_weight_reason, and student_kit_question. Help with part recognition, system classification, and evidence-based predictions. Teach names only as needed. Do not fully reveal how an off-center mass creates vibration; that is a later investigation. Ask the student how multiple parts must cooperate rather than presenting the build procedure.",
        reveal_policy:
          "Part names and broad system roles may be discussed. Do not provide the assembly sequence or fully explain off-center vibration yet.",
      },
      {
        id: "step3-completion",
        type: "completion_gate",
        rules: [
          {
            field: "accessories_pwo_opened",
            condition: "truthy",
          },
          {
            field: "inspected_image_id",
            condition: "truthy",
          },
          {
            field: "parts_found",
            condition: "min_items",
            value: 7,
          },
          {
            field: "part_systems",
            condition: "completed",
          },
          {
            field: "part_jobs",
            condition: "completed",
          },
          {
            field: "mystery_weight_hypothesis",
            condition: "answered",
          },
          {
            field: "mystery_weight_reason",
            condition: "min_length",
            value: 15,
          },
          {
            field: "student_kit_question",
            condition: "min_length",
            value: 8,
          },
        ],
      },
    ],
  },

  "plan-the-robot": {
    step_id: "plan-the-robot",
    title: "The Robot Needs a Power Plan",
    subtitle: "Plan the Robot Artist",
    phase: "Science Planning",
    story:
      "You know the parts, but a box of components does not become a robot by itself. Before building, trace the hidden energy pathway that can wake the motor, move the machine, and leave a pattern on paper.",
    blocks: [
      {
        id: "step4-story",
        type: "story_hook",
        eyebrow: "POWER PLANNING ROOM",
        title: "The robot is ready—but all its energy is still hidden",
        body:
          "Engineers plan how energy will move through a system before they connect the parts. Your challenge is to explain where the energy begins, what changes it, and how it finally becomes artwork.",
        tone: "mystery",
      },
      {
        id: "energy-start-prediction",
        type: "choice_cards",
        title: "Where does the movement story begin?",
        prompt:
          "Commit to a prediction before opening the science evidence.",
        choices: [
          {
            id: "battery",
            label: "Inside the battery, where chemical energy is stored",
          },
          {
            id: "wires",
            label: "Inside the wires, where electricity is created",
          },
          {
            id: "motor",
            label: "Inside the motor, which stores all the robot's energy",
          },
          {
            id: "markers",
            label: "Inside the markers, because they touch the paper",
          },
        ],
        save_as: "energy_start_prediction",
        required: true,
      },
      {
        id: "energy-evidence-gallery",
        type: "image_gallery",
        title: "Read the engineering evidence",
        prompt:
          "Compare the battery cutaway with the energy-conversion diagram. Look for what is stored and what is moving.",
        save_as: "energy_evidence_image",
        images: [
          {
            id: "chemical-pipeline",
            label: "Chemical energy pipeline",
            path:
              "drawing-robot/step-4/chemical_energy_pipeline.png",
            caption:
              "The battery stores chemical energy. A reaction can drive electrical energy through the system.",
            alt:
              "Battery cutaway and a chemical-to-electrical-to-motion energy pathway.",
          },
          {
            id: "rotation-conversion",
            label: "Electrical energy becomes motion",
            path:
              "drawing-robot/step-4/electrical_to_rotational_motion.png",
            caption:
              "The motor is an energy converter: electrical energy enters and rotational motion comes out.",
            alt:
              "Diagram showing chemical potential energy changing to electrical energy and then kinetic motion.",
          },
        ],
      },
      {
        id: "energy-intro-video",
        type: "video_asset",
        title: "What is energy?",
        mission:
          "Watch for the difference between stored energy and the energy of motion.",
        save_as: "energy_video_opened",
        asset: {
          id: "energy-introduction",
          type: "saras_video",
          asset_ref: {
            source_video_id: 811,
            tag_id: 23951,
          },
          provenance: {
            source_type: "collection",
            collection_id: 159,
            collection_item_id: 129,
          },
          content_context: {
            base_board: "saras",
            language_id: 1,
            board_topic_id: 747,
            source_topic_id: 569,
          },
        },
      },
      {
        id: "energy-forms-pwo",
        type: "pwo_asset",
        title: "Investigate energy changing form",
        mission:
          "Explore the energy animation. Find one example where stored energy becomes motion, then connect that example to the robot.",
        save_as: "energy_pwo_opened",
        asset: {
          id: "energy-forms-animation",
          type: "saras_pwo",
          asset_ref: {
            source_pwo_id: 515,
            scene_name: "anim_energy",
          },
          provenance: {
            source_type: "collection",
            collection_id: 159,
            collection_item_id: 129,
          },
          content_context: {
            base_board: "saras",
            language_id: 1,
            board_topic_id: 747,
            source_topic_id: 569,
          },
        },
      },
      {
        id: "battery-pwo",
        type: "pwo_asset",
        title: "Look inside the robot's power source",
        mission:
          "Explore the dry-battery model. Focus on the big idea: the battery stores chemical energy that can be released into an electrical system. The advanced chemistry is optional.",
        save_as: "battery_pwo_opened",
        asset: {
          id: "battery-drybattery",
          type: "saras_pwo",
          asset_ref: {
            source_pwo_id: 18,
            scene_name: "drybattery",
          },
          provenance: {
            source_type: "collection",
            collection_id: 159,
            collection_item_id: 137,
          },
          content_context: {
            base_board: "saras",
            language_id: 1,
            board_topic_id: 14,
            source_topic_id: 14,
          },
        },
      },
      {
        id: "energy-pathway",
        type: "sequence_builder",
        title: "Build the robot's energy pathway",
        prompt:
          "Arrange the events from hidden stored energy to visible artwork.",
        save_as: "energy_pathway",
        items: [
          {
            id: "chemical",
            label: "Chemical energy is stored in the battery",
          },
          {
            id: "electrical",
            label: "Electrical energy travels through the connected system",
          },
          {
            id: "rotation",
            label: "The motor converts electrical energy into rotation",
          },
          {
            id: "robot_motion",
            label: "Rotation helps create vibration and robot motion",
          },
          {
            id: "artwork",
            label: "The moving marker legs leave patterns on paper",
          },
        ],
        correct_order: [
          "chemical",
          "electrical",
          "rotation",
          "robot_motion",
          "artwork",
        ],
        correct_feedback:
          "Excellent. You traced energy from the battery all the way to the artwork.",
        retry_feedback:
          "Something in the pathway is out of order. Ask what must happen before the motor can rotate.",
        required: true,
      },
      {
        id: "system-energy-jobs",
        type: "matching",
        title: "Assign each system its energy job",
        prompt:
          "Match the part or system to the job it performs in the plan.",
        save_as: "system_energy_jobs",
        items: [
          {
            id: "battery",
            label: "Battery",
            options: [
              {
                id: "store_chemical",
                label: "Stores chemical energy",
              },
              {
                id: "make_art",
                label: "Directly draws the artwork",
              },
              {
                id: "create_wires",
                label: "Creates new wires",
              },
            ],
          },
          {
            id: "wires_switch",
            label: "Wires and switch",
            options: [
              {
                id: "control_path",
                label: "Connect and control the electrical path",
              },
              {
                id: "store_energy",
                label: "Store the robot's chemical energy",
              },
              {
                id: "spin_weight",
                label: "Spin the off-center weight directly",
              },
            ],
          },
          {
            id: "motor",
            label: "Motor",
            options: [
              {
                id: "convert_rotation",
                label: "Converts electrical energy into rotation",
              },
              {
                id: "hold_markers",
                label: "Holds every marker at the same height",
              },
              {
                id: "store_chemicals",
                label: "Stores chemical energy",
              },
            ],
          },
          {
            id: "body_markers",
            label: "Robot body and marker legs",
            options: [
              {
                id: "motion_to_marks",
                label: "Turn the robot's motion into visible marks",
              },
              {
                id: "create_current",
                label: "Create electric current",
              },
              {
                id: "charge_battery",
                label: "Recharge the battery during motion",
              },
            ],
          },
        ],
      },
      {
        id: "motor-energy-check",
        type: "quiz_question",
        title: "Science check",
        prompt:
          "The motor's main job is to convert electrical energy into mechanical motion.",
        choices: [
          { id: "true", label: "True" },
          { id: "false", label: "False" },
        ],
        correct_answer: "true",
        save_as: "motor_energy_check",
        correct_feedback:
          "Yes. The motor is an energy converter. Electrical energy enters, and rotational mechanical energy comes out.",
        incorrect_feedback:
          "Look again at the conversion diagram. The motor does not store the battery's chemical energy; it changes electrical energy into motion.",
      },
      {
        id: "motion-plan",
        type: "choice_cards",
        title: "Predict the robot's first motion",
        prompt:
          "This is a design prediction. You will test and revise it later.",
        choices: [
          {
            id: "tight_circles",
            label: "Mostly tight circles in one area",
          },
          {
            id: "wide_loops",
            label: "Wide wandering loops across the paper",
          },
          {
            id: "straight_lines",
            label: "Mostly straight lines",
          },
          {
            id: "not_enough_evidence",
            label: "I need more evidence before choosing",
          },
        ],
        save_as: "motion_prediction",
        required: true,
      },
      {
        id: "art-pattern-plan",
        type: "evidence_response",
        title: "Plan the artwork before the build",
        prompt:
          "What pattern do you predict the robot will draw, and which part of your energy-and-motion plan supports that prediction?",
        placeholder:
          "I predict... because the battery, motor, and moving marker legs...",
        save_as: "art_pattern_prediction",
        min_length: 25,
        required: true,
      },
      {
        id: "power-plan-evidence",
        type: "evidence_response",
        title: "Explain where movement begins",
        prompt:
          "Use evidence from the video, PWO, or diagrams. Where does the robot's movement begin, and what changes the energy into motion?",
        placeholder:
          "The movement begins with... The motor then...",
        save_as: "energy_explanation",
        min_length: 30,
        required: true,
      },
      {
        id: "step4-saras",
        type: "saras_checkpoint",
        title: "Review the power plan with Saras",
        suggested_questions: [
          "Does a battery store electricity or chemical energy?",
          "What exactly does the motor do with electrical energy?",
          "Why are the wires and switch part of the energy pathway?",
          "Help me use evidence to improve my motion prediction.",
        ],
        hidden_context:
          "The student is planning the Drawing Robot before assembly. They have identified the kit parts and are now investigating the energy pathway: chemical energy stored in the battery, electrical energy through the connected system, motor conversion to rotational mechanical energy, and eventual robot motion and artwork. Use age-appropriate language. The dry-battery PWO contains advanced chemistry; focus on the high-level energy-source idea unless the student asks for more. Do not yet teach detailed circuit troubleshooting, which belongs in Step 5, or the full off-center-mass vibration mechanism, which belongs in Step 6. Ask for predictions and evidence.",
        reveal_policy:
          "You may explain energy transformations and the motor's converter role. Preserve circuit diagnosis and wobble-mechanism discoveries for later steps.",
      },
      {
        id: "step4-completion",
        type: "completion_gate",
        incomplete_title: "Finish the robot's power plan",
        complete_title: "Power plan approved",
        incomplete_prompt:
          "Your work is saved. Finish the evidence, pathway, and predictions before the build team begins.",
        complete_prompt:
          "You traced the energy pathway and made testable motion and artwork predictions. The circuit team can begin.",
        rules: [
          { field: "energy_start_prediction", condition: "answered" },
          { field: "energy_video_opened", condition: "truthy" },
          { field: "energy_pwo_opened", condition: "truthy" },
          { field: "battery_pwo_opened", condition: "truthy" },
          { field: "energy_pathway", condition: "sequence_correct" },
          { field: "system_energy_jobs", condition: "completed" },
          {
            field: "motor_energy_check",
            condition: "equals",
            value: "true",
          },
          { field: "motion_prediction", condition: "answered" },
          {
            field: "art_pattern_prediction",
            condition: "min_length",
            value: 25,
          },
          {
            field: "energy_explanation",
            condition: "min_length",
            value: 30,
          },
        ],
      },
    ],
  },

  "build-the-circuit": {
    step_id: "build-the-circuit",
    title: "The Broken Electricity Road",
    subtitle: "Build and Test the Circuit",
    phase: "Circuit",
    story:
      "The battery and motor are ready, but the motor will not run unless electricity has a complete road. Your team must build the loop, find possible breaks, and test it safely.",
    blocks: [
      {
        id: "step5-story",
        type: "story_hook",
        eyebrow: "SYSTEM ALERT",
        title: "Power is available—but the motor is silent",
        body:
          "A battery can store energy, but stored energy alone cannot reach the motor. Somewhere between the battery and motor, the electrical road must become a complete loop.",
        tone: "mystery",
      },
      {
        id: "circuit-images",
        type: "image_gallery",
        title: "Inspect the circuit evidence",
        prompt:
          "Compare the complete road with the broken road. Then inspect the troubleshooting dashboard.",
        images: [
          {
            id: "closed-open-road",
            title: "Complete road and broken road",
            path: "drawing-robot/step-5/closed_vs_open_circuit.png",
            alt:
              "A visual comparison of a glowing closed electrical loop and a broken open loop.",
          },
          {
            id: "troubleshooting",
            title: "Circuit detective dashboard",
            path: "drawing-robot/step-5/circuit_troubleshooting.png",
            alt:
              "A drawing robot circuit with a checklist for diagnosing a broken electrical path.",
          },
        ],
        save_as: "selected_circuit_evidence",
      },
      {
        id: "circuit-first-prediction",
        type: "choice_cards",
        title: "Where would you investigate first?",
        prompt:
          "The motor is silent. Choose your first circuit check before receiving the explanation.",
        choices: [
          { id: "battery_contact", label: "Check the battery contacts" },
          { id: "switch", label: "Check whether the switch is closed" },
          { id: "loose_wire", label: "Check for a loose wire" },
          { id: "motor_blocked", label: "Check whether the motor shaft is blocked" },
          { id: "trace_loop", label: "Trace the entire loop in order" },
        ],
        save_as: "first_circuit_check",
        required: true,
      },
      {
        id: "circuit-video",
        type: "video_asset",
        title: "Electricity Traffic Needs a Complete Road",
        mission:
          "Watch for the difference between stored energy and a complete path for current.",
        save_as: "circuit_video_opened",
        asset: {
          id: "drawing-robot-circuit-road-video",
          type: "saras_video",
          asset_ref: {
            source_video_id: 7024,
            tag_id: 30041,
          },
          provenance: {
            source_type: "collection",
            collection_id: 159,
            collection_item_id: 136,
          },
          content_context: {
            base_board: "saras",
            language_id: 1,
            board_topic_id: 933,
            source_topic_id: 959,
          },
        },
      },
      {
        id: "closed-switch-pwo",
        type: "pwo_asset",
        title: "Open and Close the Electrical Road",
        mission:
          "Use the switch scene to observe when the electrical path becomes complete.",
        save_as: "circuit_pwo_opened",
        asset: {
          id: "electricity-pow-keyclose",
          type: "saras_pwo",
          asset_ref: {
            source_pwo_id: 467,
            scene_name: "pow_keyclose",
          },
          provenance: {
            source_type: "collection",
            collection_id: 159,
            collection_item_id: 134,
          },
          content_context: {
            base_board: "saras",
            language_id: 1,
            board_topic_id: 635,
            source_topic_id: 486,
          },
        },
      },
      {
        id: "circuit-path-sequence",
        type: "sequence_builder",
        title: "Trace one complete loop",
        prompt:
          "Place the components in the order that lets you trace a continuous path from the battery and back again.",
        save_as: "circuit_path",
        items: [
          { id: "battery_out", label: "Battery terminal sends energy into the circuit" },
          { id: "switch", label: "Closed switch allows the path to continue" },
          { id: "motor", label: "Motor receives electrical energy" },
          { id: "return_wire", label: "Return wire completes the loop to the battery" },
        ],
        correct_order: [
          "battery_out",
          "switch",
          "motor",
          "return_wire",
        ],
        correct_feedback:
          "Yes. A complete loop provides an unbroken path through every required component.",
        retry_feedback:
          "Trace the path with your finger. Where does the current go after leaving the battery?",
        required: true,
      },
      {
        id: "circuit-jobs",
        type: "matching",
        title: "Assign the circuit jobs",
        prompt:
          "Match each component to the role it plays in the electrical road.",
        save_as: "circuit_jobs",
        items: [
          {
            id: "battery",
            label: "Battery",
            options: [
              { id: "source", label: "Provides the potential difference and energy source" },
              { id: "opens_path", label: "Opens and closes the path" },
              { id: "rotation", label: "Creates rotation directly" },
            ],
          },
          {
            id: "switch",
            label: "Switch",
            options: [
              { id: "control", label: "Controls whether the path is open or closed" },
              { id: "store", label: "Stores chemical energy" },
              { id: "draw", label: "Makes marks on paper" },
            ],
          },
          {
            id: "wires",
            label: "Wires",
            options: [
              { id: "connect", label: "Connect the components into a continuous path" },
              { id: "balance", label: "Balance the marker legs" },
              { id: "vibrate", label: "Create wobble without a motor" },
            ],
          },
          {
            id: "motor",
            label: "Motor",
            options: [
              { id: "convert", label: "Converts electrical energy into rotation" },
              { id: "charge", label: "Recharges the battery" },
              { id: "switch_job", label: "Decides whether the circuit is open" },
            ],
          },
        ],
      },
      {
        id: "open-wire-check",
        type: "quiz_question",
        title: "Circuit detective check",
        prompt:
          "One wire is removed while the robot is running. What should happen?",
        choices: [
          { id: "faster", label: "The motor should spin faster" },
          { id: "stop", label: "The motor should stop because the loop is open" },
          { id: "same", label: "Nothing should change" },
          { id: "reverse", label: "The motor should reverse direction" },
        ],
        correct_answer: "stop",
        save_as: "open_wire_check",
        correct_feedback:
          "Correct. Removing the wire breaks the continuous path, so current cannot flow through the motor.",
        incorrect_feedback:
          "Recheck the open-circuit image. A broken road stops the electrical traffic.",
      },
      {
        id: "physical-circuit-checklist",
        type: "build_checklist",
        title: "Build and test the real circuit",
        prompt:
          "Complete these checks with the switch off until the final brief test.",
        save_as: "circuit_build_checks",
        statuses: [
          { id: "complete", label: "Completed" },
          { id: "help", label: "I need help" },
          { id: "not_yet", label: "Not yet" },
        ],
        items: [
          {
            id: "battery_inserted",
            label: "Battery is inserted in the correct direction",
            help: "Match the positive and negative markings.",
          },
          {
            id: "switch_off",
            label: "Switch remains off while connections are being made",
            help: "This prevents unexpected motor movement.",
          },
          {
            id: "wires_secure",
            label: "Both wires are attached securely",
            help: "A loose wire creates an open circuit.",
          },
          {
            id: "motor_free",
            label: "Motor shaft and attached weight can turn freely",
            help: "Electrical success can be hidden by a mechanical blockage.",
          },
          {
            id: "brief_test",
            label: "Circuit is switched on briefly and the motor spins",
            help: "Switch it off after confirming the test.",
          },
        ],
      },
      {
        id: "circuit-evidence",
        type: "evidence_response",
        title: "Write the diagnosis",
        prompt:
          "How would you distinguish an open-circuit problem from a motor that is physically blocked?",
        placeholder:
          "I would first... If the circuit is complete but the motor still...",
        save_as: "circuit_diagnosis",
        min_length: 30,
        required: true,
      },
      {
        id: "step5-saras",
        type: "saras_checkpoint",
        title: "Review the circuit with Saras",
        suggested_questions: [
          "How can I trace the whole circuit without guessing?",
          "Why does a loose wire stop the motor?",
          "How is an open circuit different from a blocked motor?",
          "What should I check before turning the switch on?",
        ],
        hidden_context:
          "The student is completing the Drawing Robot circuit step. They have already learned the high-level energy pathway. Help them reason about a complete closed electrical path, battery, switch, wires, and motor. Distinguish electrical breaks from mechanical blockage. Give one diagnostic check at a time. Do not yet reveal the full off-center-mass vibration mechanism; that belongs in Step 6. Prioritize safe brief testing and switching off before changing connections.",
        reveal_policy:
          "You may explain closed and open circuits and safe troubleshooting. Preserve the deeper vibration discovery for Step 6.",
      },
      {
        id: "step5-completion",
        type: "completion_gate",
        incomplete_title: "Finish the circuit investigation",
        complete_title: "Electrical road verified",
        incomplete_prompt:
          "Complete the circuit evidence and physical checks before investigating the robot's unusual motion.",
        complete_prompt:
          "You built a complete path and can distinguish electrical and mechanical failures.",
        rules: [
          { field: "first_circuit_check", condition: "answered" },
          { field: "circuit_video_opened", condition: "truthy" },
          { field: "circuit_pwo_opened", condition: "truthy" },
          { field: "circuit_path", condition: "sequence_correct" },
          { field: "circuit_jobs", condition: "completed" },
          { field: "open_wire_check", condition: "equals", value: "stop" },
          {
            field: "circuit_build_checks",
            condition: "all_equal",
            value: "complete",
          },
          {
            field: "circuit_diagnosis",
            condition: "min_length",
            value: 30,
          },
        ],
      },
    ],
  },

  "create-the-vibration": {
    step_id: "create-the-vibration",
    title: "The Secret of Wobble-Power",
    subtitle: "From Rotation to Vibration",
    phase: "Motion",
    story:
      "The circuit works and the motor spins—but smooth rotation alone does not explain the robot's strange dance. The unusual weight on the motor may be the clue that turns rotation into vibration.",
    blocks: [
      {
        id: "step6-story",
        type: "story_hook",
        eyebrow: "MOTION MYSTERY",
        title: "The motor spins. Why does the whole robot shake?",
        body:
          "A perfectly centered spinning weight would pull evenly in every direction. This robot uses an intentionally uneven weight. Predict what that changes before seeing the explanation.",
        tone: "mystery",
      },
      {
        id: "wobble-images",
        type: "image_gallery",
        title: "Compare centered and off-center motion",
        prompt:
          "Study the motor diagrams and the real-world examples of uneven rotating loads.",
        images: [
          {
            id: "eccentric-motor",
            title: "The imbalance engine",
            path: "drawing-robot/step-6/eccentric_vibration.png",
            alt:
              "A motor diagram showing an off-center weight attached to the shaft.",
          },
          {
            id: "balanced-unbalanced",
            title: "Balanced versus off-center rotation",
            path: "drawing-robot/step-6/centrifugal_imbalance.png",
            alt:
              "A comparison of a balanced spinning weight and an off-center spinning weight.",
          },
          {
            id: "real-world-wobble",
            title: "Wobble-power in the real world",
            path: "drawing-robot/step-6/wobble_real_world.png",
            alt:
              "Examples of vibration in a game controller and an uneven washing-machine load.",
          },
        ],
        save_as: "selected_wobble_evidence",
      },
      {
        id: "balanced-offcenter-prediction",
        type: "choice_cards",
        title: "Which design should create stronger vibration?",
        prompt:
          "Lock in your prediction before the video and simulations.",
        choices: [
          { id: "centered", label: "A perfectly centered weight" },
          { id: "off_center", label: "A weight mounted away from the center" },
          { id: "same", label: "Both should vibrate equally" },
          { id: "need_evidence", label: "I need more evidence" },
        ],
        save_as: "wobble_prediction",
        required: true,
      },
      {
        id: "dancing-robot-video",
        type: "video_asset",
        title: "The Physics of the Dancing Robot",
        mission:
          "Watch for how rotating imbalance becomes a repeated changing force on the robot.",
        save_as: "dancing_robot_video_opened",
        asset: {
          id: "drawing-robot-dancing-physics",
          type: "saras_video",
          asset_ref: {
            source_video_id: 7025,
            tag_id: 30042,
          },
          provenance: {
            source_type: "collection",
            collection_id: 159,
            collection_item_id: 136,
          },
          content_context: {
            base_board: "saras",
            language_id: 1,
            board_topic_id: 933,
            source_topic_id: 959,
          },
        },
      },
      {
        id: "motor-animation-pwo",
        type: "pwo_asset",
        title: "Inspect the Motor's Rotation",
        mission:
          "Observe how a motor converts electrical energy into rotation before the imbalance is added.",
        save_as: "motor_animation_opened",
        asset: {
          id: "dc-motor-animation",
          type: "saras_pwo",
          asset_ref: {
            source_pwo_id: 360,
            scene_name: "dc_motoranim",
          },
          provenance: {
            source_type: "collection",
            collection_id: 159,
            collection_item_id: 135,
          },
          content_context: {
            base_board: "saras",
            language_id: 1,
            board_topic_id: 163,
            source_topic_id: 353,
          },
        },
      },
      {
        id: "inertia-pwo",
        type: "pwo_asset",
        title: "Investigate Inertia",
        mission:
          "Explore why an object at rest needs an unbalanced force to change its motion.",
        save_as: "inertia_pwo_opened",
        asset: {
          id: "law-of-inertia",
          type: "saras_pwo",
          asset_ref: {
            source_pwo_id: 536,
            scene_name: "inertia",
          },
          provenance: {
            source_type: "collection",
            collection_id: 159,
            collection_item_id: 130,
          },
          content_context: {
            base_board: "saras",
            language_id: 1,
            board_topic_id: 738,
            source_topic_id: 560,
          },
        },
      },
      {
        id: "wobble-sequence",
        type: "sequence_builder",
        title: "Build the wobble mechanism",
        prompt:
          "Arrange the cause-and-effect chain from motor rotation to robot movement.",
        save_as: "wobble_sequence",
        items: [
          { id: "motor_rotates", label: "The motor shaft rotates" },
          { id: "weight_changes_direction", label: "The off-center weight continually changes direction" },
          { id: "uneven_force", label: "The changing imbalance produces an uneven changing force" },
          { id: "body_vibrates", label: "The robot body vibrates" },
          { id: "marker_contact", label: "Marker legs push and slip against the paper" },
          { id: "robot_moves", label: "The robot changes position and leaves a pattern" },
        ],
        correct_order: [
          "motor_rotates",
          "weight_changes_direction",
          "uneven_force",
          "body_vibrates",
          "marker_contact",
          "robot_moves",
        ],
        correct_feedback:
          "Exactly. The off-center rotating mass creates a repeating changing force that travels through the robot.",
        retry_feedback:
          "Begin with the motor shaft. What is the first new effect caused by the off-center weight?",
        required: true,
      },
      {
        id: "motion-causes",
        type: "matching",
        title: "Match each design choice to its likely effect",
        prompt:
          "These are hypotheses. You will test them during the art trials.",
        save_as: "motion_causes",
        items: [
          {
            id: "centered_weight",
            label: "Weight close to the shaft center",
            options: [
              { id: "gentler", label: "Usually gentler vibration" },
              { id: "stronger", label: "Always the strongest vibration" },
              { id: "open_circuit", label: "Creates an open circuit" },
            ],
          },
          {
            id: "farther_weight",
            label: "Weight farther from the shaft center",
            options: [
              { id: "stronger_wobble", label: "Can create stronger wobble" },
              { id: "less_energy", label: "Stops the battery storing energy" },
              { id: "no_rotation", label: "Prevents motor rotation" },
            ],
          },
          {
            id: "marker_contact",
            label: "Marker tips touching the paper",
            options: [
              { id: "forces_motion", label: "Provide contact forces that shape motion and marks" },
              { id: "store_energy", label: "Store chemical energy" },
              { id: "close_switch", label: "Close the electrical switch" },
            ],
          },
        ],
      },
      {
        id: "center-weight-check",
        type: "quiz_question",
        title: "Wobble check",
        prompt:
          "A perfectly centered spinning weight should usually create less vibration than the same weight mounted off-center.",
        choices: [
          { id: "true", label: "True" },
          { id: "false", label: "False" },
        ],
        correct_answer: "true",
        save_as: "center_weight_check",
        correct_feedback:
          "Yes. A centered rotating mass is more balanced; an off-center mass creates changing imbalance.",
        incorrect_feedback:
          "Compare the balanced and eccentric diagrams again. The off-center design creates the uneven pull.",
      },
      {
        id: "wobble-evidence",
        type: "evidence_response",
        title: "Explain the dance",
        prompt:
          "Why can a small off-center weight make the entire robot move? Use the words rotation, imbalance, force, and vibration.",
        placeholder:
          "As the motor rotates... Because the weight is off-center...",
        save_as: "wobble_explanation",
        min_length: 40,
        required: true,
      },
      {
        id: "step6-saras",
        type: "saras_checkpoint",
        title: "Challenge your wobble theory with Saras",
        suggested_questions: [
          "Why does an off-center weight create vibration?",
          "How does Newton's first law connect to the robot starting to move?",
          "Would stronger vibration always make better art?",
          "How do the marker tips help turn vibration into travel?",
        ],
        hidden_context:
          "The student has a working circuit and is investigating how motor rotation becomes vibration and robot motion. Help them reason about centered versus off-center rotating mass, changing unbalanced force, inertia, vibration, and marker contact. Use age-appropriate language and ask for evidence. Do not claim that maximum vibration is automatically best; stability and friction will be explored later. The student should be able to connect motor rotation to off-center imbalance and body vibration.",
        reveal_policy:
          "You may fully explain the off-center-mass vibration mechanism. Preserve detailed friction tuning and controlled redesign for Step 8.",
      },
      {
        id: "step6-completion",
        type: "completion_gate",
        incomplete_title: "Finish the wobble investigation",
        complete_title: "Wobble mechanism explained",
        incomplete_prompt:
          "Complete the motion evidence before assembling and launching the full robot.",
        complete_prompt:
          "You connected motor rotation, imbalance, changing force, vibration, and marker contact.",
        rules: [
          { field: "wobble_prediction", condition: "answered" },
          { field: "dancing_robot_video_opened", condition: "truthy" },
          { field: "motor_animation_opened", condition: "truthy" },
          { field: "inertia_pwo_opened", condition: "truthy" },
          { field: "wobble_sequence", condition: "sequence_correct" },
          { field: "motion_causes", condition: "completed" },
          { field: "center_weight_check", condition: "equals", value: "true" },
          {
            field: "wobble_explanation",
            condition: "min_length",
            value: 40,
          },
        ],
      },
    ],
  },

  "first-art-test": {
    step_id: "first-art-test",
    title: "The Maiden Voyage",
    subtitle: "Assemble the Robot and Run the First Art Test",
    phase: "Prototype",
    story:
      "The science plan is ready. Now you will assemble the physical robot, complete a careful preflight check, predict its first movement, and treat the resulting artwork as engineering evidence.",
    blocks: [
      {
        id: "step7-story",
        type: "story_hook",
        eyebrow: "PROTOTYPE LAUNCH",
        title: "Build carefully. Predict before switching on.",
        body:
          "This is not merely an assembly exercise. At every phase, connect the physical part to the energy, circuit, and motion ideas you already investigated.",
        tone: "mystery",
      },
      {
        id: "assembly-gallery",
        type: "image_gallery",
        title: "Study the assembly evidence",
        prompt:
          "Use the virtual guide and real photographs to compare each phase with your physical kit.",
        images: [
          {
            id: "pwo-installation",
            title: "Virtual assembly guide",
            path: "drawing-robot/step-7/drawing_robot_pwo_assembly.png",
            alt:
              "The Drawing Robot Unity PWO showing the installation and assembly scene.",
          },
          {
            id: "assembly-1-2",
            title: "Assembly phases 1–2",
            path: "drawing-robot/step-7/assembly_1_2.jpg",
            alt:
              "Early Drawing Robot assembly steps with the base and marker supports.",
          },
          {
            id: "assembly-3-4",
            title: "Assembly phases 3–4",
            path: "drawing-robot/step-7/assembly_3_4.jpg",
            alt:
              "Drawing Robot assembly steps showing components being mounted.",
          },
          {
            id: "assembly-5-6",
            title: "Assembly phases 5–6",
            path: "drawing-robot/step-7/assembly_5_6.jpg",
            alt:
              "Drawing Robot assembly steps showing motor and electrical parts.",
          },
          {
            id: "assembly-7-8",
            title: "Assembly phases 7–8",
            path: "drawing-robot/step-7/assembly_7_8.jpg",
            alt:
              "Later Drawing Robot assembly steps and wiring checks.",
          },
          {
            id: "assembly-9-10",
            title: "Assembly phases 9–10",
            path: "drawing-robot/step-7/assembly_9_10.jpg",
            alt:
              "Final Drawing Robot assembly and alignment checks.",
          },
          {
            id: "maiden-voyage",
            title: "Maiden voyage",
            path: "drawing-robot/step-7/maiden_voyage.png",
            alt:
              "A completed Drawing Robot making colored looping patterns during its first launch.",
          },
        ],
        save_as: "selected_assembly_evidence",
      },
      {
        id: "assembly-video",
        type: "video_asset",
        title: "Engineering the Doodle Robot",
        mission:
          "Watch for how the parts form one working system. Pause before copying any step you do not understand.",
        save_as: "assembly_video_opened",
        asset: {
          id: "drawing-robot-engineering-video",
          type: "saras_video",
          asset_ref: {
            source_video_id: 7031,
            tag_id: 30048,
          },
          provenance: {
            source_type: "collection",
            collection_id: 159,
            collection_item_id: 136,
          },
          content_context: {
            base_board: "saras",
            language_id: 1,
            board_topic_id: 933,
            source_topic_id: 959,
          },
        },
      },
      {
        id: "installation-pwo",
        type: "pwo_asset",
        title: "Open the Interactive Assembly Guide",
        mission:
          "Use the installation scene as a comparison model while building the physical prototype.",
        save_as: "installation_pwo_opened",
        asset: {
          id: "drawing-robot-installation",
          type: "saras_pwo",
          asset_ref: {
            source_pwo_id: 1168,
            scene_name: "installation",
          },
          provenance: {
            source_type: "collection",
            collection_id: 159,
            collection_item_id: 136,
          },
          content_context: {
            base_board: "saras",
            language_id: 1,
            board_topic_id: 933,
            source_topic_id: 959,
          },
        },
      },
      {
        id: "assembly-phase-checklist",
        type: "build_checklist",
        title: "Prototype assembly tracker",
        prompt:
          "Do not mark a phase complete until the physical robot matches the evidence and passes the stated check.",
        save_as: "assembly_phases",
        statuses: [
          { id: "complete", label: "Completed" },
          { id: "help", label: "I need help" },
          { id: "not_yet", label: "Not yet" },
        ],
        items: [
          {
            id: "base_markers",
            label: "Base and marker legs assembled",
            help: "Marker tips should reach the paper and support the base.",
          },
          {
            id: "battery_switch",
            label: "Battery holder and switch mounted securely",
            help: "The switch must remain accessible.",
          },
          {
            id: "motor_weight",
            label: "Motor and off-center weight mounted securely",
            help: "The weight must rotate without striking the body.",
          },
          {
            id: "wiring",
            label: "Electrical path connected and inspected",
            help: "Trace battery → switch → motor → return path.",
          },
          {
            id: "level_stance",
            label: "Marker stance and body alignment checked",
            help: "All marker tips should contact the paper without severe tipping.",
          },
          {
            id: "preflight",
            label: "Loose parts removed and test area cleared",
            help: "Use a large sheet of paper and keep hands away from the rotating weight.",
          },
        ],
      },
      {
        id: "launch-prediction",
        type: "choice_cards",
        title: "Predict the maiden voyage",
        prompt:
          "Choose the motion you expect before switching the robot on.",
        choices: [
          { id: "tight_spin", label: "Tight spinning in one area" },
          { id: "wide_wander", label: "Wide wandering loops" },
          { id: "mostly_still", label: "Vibrates but mostly stays in one place" },
          { id: "tips", label: "Tips or drags one marker" },
          { id: "need_evidence", label: "I cannot predict yet" },
        ],
        save_as: "launch_prediction",
        required: true,
      },
      {
        id: "first-test-record",
        type: "experiment_record",
        title: "Record Test 1",
        prompt:
          "Observe first. Explain later. Record what the robot and artwork actually did.",
        save_as: "test1_record",
        fields: [
          {
            id: "upright",
            type: "choice",
            label: "Did the robot remain upright?",
            choices: [
              { id: "yes", label: "Yes" },
              { id: "partly", label: "Partly" },
              { id: "no", label: "No" },
            ],
          },
          {
            id: "travel",
            type: "choice",
            label: "How did it travel?",
            choices: [
              { id: "wide", label: "Moved widely" },
              { id: "spin", label: "Spun mostly in one area" },
              { id: "small", label: "Moved only a little" },
              { id: "stopped", label: "Did not move properly" },
            ],
          },
          {
            id: "pattern",
            type: "choice",
            label: "What pattern appeared?",
            choices: [
              { id: "tight_loops", label: "Tight loops" },
              { id: "wide_loops", label: "Wide loops" },
              { id: "zigzag", label: "Zigzags or wandering lines" },
              { id: "uneven", label: "Uneven or incomplete marks" },
              { id: "mixed", label: "Mixed pattern" },
            ],
          },
          {
            id: "surprise",
            type: "text",
            label: "What was the biggest surprise?",
            placeholder: "I expected... but observed...",
            min_length: 15,
          },
        ],
      },
      {
        id: "drawing-as-data",
        type: "evidence_response",
        title: "Read the artwork as data",
        prompt:
          "What does the pattern suggest about the robot's motion, balance, or marker contact?",
        placeholder:
          "The pattern suggests... because the lines...",
        save_as: "test1_evidence",
        min_length: 30,
        required: true,
      },
      {
        id: "mapping-wobble-video",
        type: "video_asset",
        title: "Mapping the Wobble",
        mission:
          "After your first test, compare the video's motion-to-pattern clues with your own artwork.",
        save_as: "mapping_video_opened",
        asset: {
          id: "drawing-robot-mapping-wobble",
          type: "saras_video",
          asset_ref: {
            source_video_id: 7027,
            tag_id: 30044,
          },
          provenance: {
            source_type: "collection",
            collection_id: 159,
            collection_item_id: 136,
          },
          content_context: {
            base_board: "saras",
            language_id: 1,
            board_topic_id: 933,
            source_topic_id: 959,
          },
        },
      },
      {
        id: "step7-saras",
        type: "saras_checkpoint",
        title: "Debrief the first test with Saras",
        suggested_questions: [
          "What can my drawing pattern reveal about the robot's motion?",
          "Why might one marker drag more than the others?",
          "How can I tell whether the problem is electrical or mechanical?",
          "Which single variable should I consider changing next?",
        ],
        hidden_context:
          "The student has assembled and physically tested the Drawing Robot. Help them separate observation from explanation. Use their Test 1 record and artwork evidence. Troubleshoot safely, one check at a time. Do not propose changing several variables simultaneously. The next step is controlled improvement using friction, balance, marker stance, and wobble tuning.",
        reveal_policy:
          "You may troubleshoot the assembled prototype and interpret patterns. Encourage one-variable testing rather than giving a complete redesign.",
      },
      {
        id: "step7-completion",
        type: "completion_gate",
        incomplete_title: "Complete the first prototype test",
        complete_title: "Test 1 recorded",
        incomplete_prompt:
          "Finish assembly, run a safe test, and record observations before redesigning.",
        complete_prompt:
          "You built the prototype and captured evidence from its first motion and artwork.",
        rules: [
          { field: "assembly_video_opened", condition: "truthy" },
          { field: "installation_pwo_opened", condition: "truthy" },
          {
            field: "assembly_phases",
            condition: "all_equal",
            value: "complete",
          },
          { field: "launch_prediction", condition: "answered" },
          { field: "test1_record", condition: "record_complete" },
          {
            field: "test1_evidence",
            condition: "min_length",
            value: 30,
          },
          { field: "mapping_video_opened", condition: "truthy" },
        ],
      },
    ],
  },

  "engineering-improvement": {
    step_id: "engineering-improvement",
    title: "One Change, Better Evidence",
    subtitle: "Improve the Design",
    phase: "Redesign",
    story:
      "The first drawing is not a final grade. It is data. Your engineering team must choose exactly one variable, predict the result, run Test 2, and compare the evidence fairly.",
    blocks: [
      {
        id: "step8-story",
        type: "story_hook",
        eyebrow: "REDESIGN BRIEF",
        title: "Do not change everything at once",
        body:
          "Engineers learn more when they change one important variable while keeping the rest of the system as consistent as possible. A prettier picture is not automatically a better test.",
        tone: "mystery",
      },
      {
        id: "test1-case-file",
        type: "prior_work_summary",
        title: "Reopen the Test 1 case file",
        prompt:
          "Use your earlier observation as the baseline for the redesign.",
        items: [
          {
            source_step_id: "first-art-test",
            field: "launch_prediction",
            label: "Original prediction",
            value_labels: {
              tight_spin: "Tight spinning in one area",
              wide_wander: "Wide wandering loops",
              mostly_still: "Vibrates but mostly stays in one place",
              tips: "Tips or drags one marker",
              need_evidence: "Could not predict yet",
            },
          },
          {
            source_step_id: "first-art-test",
            field: "test1_record.travel",
            label: "Observed travel",
            value_labels: {
              wide: "Moved widely",
              spin: "Spun mostly in one area",
              small: "Moved only a little",
              stopped: "Did not move properly",
            },
          },
          {
            source_step_id: "first-art-test",
            field: "test1_record.pattern",
            label: "Observed pattern",
            value_labels: {
              tight_loops: "Tight loops",
              wide_loops: "Wide loops",
              zigzag: "Zigzags or wandering lines",
              uneven: "Uneven or incomplete marks",
              mixed: "Mixed pattern",
            },
          },
          {
            source_step_id: "first-art-test",
            field: "test1_record.surprise",
            label: "Biggest surprise",
          },
          {
            source_step_id: "first-art-test",
            field: "test1_evidence",
            label: "Evidence interpretation",
          },
        ],
      },
      {
        id: "redesign-gallery",
        type: "image_gallery",
        title: "Study the redesign clues",
        prompt:
          "Look for how wobble, friction, balance, and marker geometry can change the motion-to-art result.",
        images: [
          {
            id: "control-chaos",
            title: "Controlling the chaos",
            path: "drawing-robot/step-8/controlling_the_chaos.png",
            alt:
              "A design matrix connecting wobble, paper type, and leg spacing to drawing patterns.",
          },
          {
            id: "friction-battle",
            title: "Vibration versus friction",
            path: "drawing-robot/step-8/friction_battle.png",
            alt:
              "A marker tip showing vibration overcoming static friction on paper.",
          },
          {
            id: "engineer-matrix",
            title: "Master engineer matrix",
            path: "drawing-robot/step-8/master_engineer_matrix.png",
            alt:
              "A matrix relating marker height and weight position to different robot art patterns.",
          },
        ],
        save_as: "selected_redesign_evidence",
      },
      {
        id: "redesign-variable",
        type: "choice_cards",
        title: "Choose exactly one variable",
        prompt:
          "Select the single change that best addresses your Test 1 evidence.",
        choices: [
          { id: "marker_angle", label: "Marker angle" },
          { id: "marker_height", label: "Marker height or paper contact" },
          { id: "leg_spacing", label: "Spacing between marker legs" },
          { id: "weight_position", label: "Off-center weight position" },
          { id: "motor_position", label: "Motor position or security" },
          { id: "battery_balance", label: "Battery position and body balance" },
          { id: "paper_surface", label: "Paper or testing surface" },
          { id: "secure_wiring", label: "Secure a loose electrical connection" },
        ],
        save_as: "selected_design_variable",
        required: true,
      },
      {
        id: "redesign-prediction",
        type: "evidence_response",
        title: "Commit your Test 2 prediction",
        prompt:
          "State what you will change, what you will keep the same, and what effect you predict.",
        placeholder:
          "I will change... I will keep... I predict the robot will... because...",
        save_as: "redesign_prediction",
        min_length: 35,
        required: true,
      },
      {
        id: "redesign-video",
        type: "video_asset",
        title: "Redesigning for the Perfect Dance",
        mission:
          "Watch for the difference between random tinkering and a controlled engineering change.",
        save_as: "redesign_video_opened",
        asset: {
          id: "drawing-robot-redesign-perfect-dance",
          type: "saras_video",
          asset_ref: {
            source_video_id: 7036,
            tag_id: 30053,
          },
          provenance: {
            source_type: "collection",
            collection_id: 159,
            collection_item_id: 136,
          },
          content_context: {
            base_board: "saras",
            language_id: 1,
            board_topic_id: 933,
            source_topic_id: 959,
          },
        },
      },
      {
        id: "wobble-power-video",
        type: "video_asset",
        title: "Optional: Master Your Wobble Power",
        mission:
          "Use this deeper station when your chosen variable involves wobble strength, balance, or marker contact.",
        asset: {
          id: "drawing-robot-master-wobble-power",
          type: "saras_video",
          asset_ref: {
            source_video_id: 7035,
            tag_id: 30052,
          },
          provenance: {
            source_type: "collection",
            collection_id: 159,
            collection_item_id: 136,
          },
          content_context: {
            base_board: "saras",
            language_id: 1,
            board_topic_id: 933,
            source_topic_id: 959,
          },
        },
      },
      {
        id: "friction-station",
        type: "pwo_asset",
        title: "Optional Friction Science Station",
        mission:
          "Use this station when paper contact, marker drag, or surface choice is central to your redesign.",
        asset: {
          id: "friction-procedure",
          type: "saras_pwo",
          asset_ref: {
            source_pwo_id: 1064,
            scene_name: "procedure",
          },
          provenance: {
            source_type: "collection",
            collection_id: 159,
            collection_item_id: 131,
          },
          content_context: {
            base_board: "saras",
            language_id: 1,
            board_topic_id: 821,
            source_topic_id: 816,
          },
        },
      },
      {
        id: "test2-record",
        type: "experiment_record",
        title: "Record Test 2",
        prompt:
          "Observe before explaining. Record the redesigned robot's actual behavior.",
        save_as: "test2_record",
        fields: [
          {
            id: "upright",
            type: "choice",
            label: "Did the robot remain upright?",
            choices: [
              { id: "yes", label: "Yes" },
              { id: "partly", label: "Partly" },
              { id: "no", label: "No" },
            ],
          },
          {
            id: "travel",
            type: "choice",
            label: "How did it travel?",
            choices: [
              { id: "wide", label: "Moved widely" },
              { id: "spin", label: "Spun mostly in one area" },
              { id: "small", label: "Moved only a little" },
              { id: "stopped", label: "Did not move properly" },
            ],
          },
          {
            id: "pattern",
            type: "choice",
            label: "What pattern appeared?",
            choices: [
              { id: "tight_loops", label: "Tight loops" },
              { id: "wide_loops", label: "Wide loops" },
              { id: "zigzag", label: "Zigzags or wandering lines" },
              { id: "uneven", label: "Uneven or incomplete marks" },
              { id: "mixed", label: "Mixed pattern" },
            ],
          },
          {
            id: "surprise",
            type: "text",
            label: "What changed or surprised you?",
            placeholder: "Compared with Test 1...",
            min_length: 15,
          },
        ],
      },
      {
        id: "improvement-conclusion",
        type: "choice_cards",
        title: "Judge the redesign against your goal",
        prompt:
          "Choose the conclusion best supported by your two tests.",
        choices: [
          { id: "improved", label: "Improved for our stated goal" },
          { id: "worse", label: "Performed worse for our stated goal" },
          { id: "different_useful", label: "Different, but useful in another way" },
          { id: "not_enough", label: "Not enough evidence yet" },
        ],
        save_as: "redesign_conclusion",
        required: true,
      },
      {
        id: "test-comparison",
        type: "evidence_response",
        title: "Defend the conclusion with evidence",
        prompt:
          "Compare Test 1 and Test 2. What changed, what stayed similar, and what evidence supports your conclusion?",
        placeholder:
          "In Test 1... After changing only... Test 2 showed... Therefore...",
        save_as: "test_comparison",
        min_length: 45,
        required: true,
      },
      {
        id: "step8-saras",
        type: "saras_checkpoint",
        title: "Review the redesign with Saras",
        suggested_questions: [
          "Is my design change really only one variable?",
          "How can I compare Test 1 and Test 2 fairly?",
          "What does the artwork reveal about friction or balance?",
          "My second test was worse. Did I still learn something useful?",
        ],
        hidden_context:
          "The student is conducting a controlled redesign of the Drawing Robot after Test 1. Use their selected variable, prediction, Test 2 record, and comparison. Emphasize changing one variable at a time, judging against the student's stated goal, and treating worse or unexpected results as useful evidence. Do not equate prettier artwork with better engineering. Offer one troubleshooting or analysis question at a time.",
        reveal_policy:
          "You may discuss friction, balance, marker geometry, wobble strength, and controlled testing. Preserve the student's ownership of the redesign conclusion.",
      },
      {
        id: "step8-completion",
        type: "completion_gate",
        incomplete_title: "Complete the controlled redesign",
        complete_title: "Test 2 compared",
        incomplete_prompt:
          "Choose one variable, make a prediction, run Test 2, and compare the evidence.",
        complete_prompt:
          "You completed a controlled engineering iteration and defended the result with evidence.",
        rules: [
          { field: "selected_design_variable", condition: "answered" },
          {
            field: "redesign_prediction",
            condition: "min_length",
            value: 35,
          },
          { field: "redesign_video_opened", condition: "truthy" },
          { field: "test2_record", condition: "record_complete" },
          { field: "redesign_conclusion", condition: "answered" },
          {
            field: "test_comparison",
            condition: "min_length",
            value: 45,
          },
        ],
      },
    ],
  },

  "explain-the-science": {
    step_id: "explain-the-science",
    title: "Solve the Whole Machine",
    subtitle: "Explain How the Robot Works",
    phase: "Explain",
    story:
      "The mystery is nearly solved. Now connect every subsystem into one cause-and-effect explanation—from stored energy in the battery to visible marks on the paper.",
    blocks: [
      {
        id: "step9-story",
        type: "story_hook",
        eyebrow: "ENGINEER'S DEFENSE",
        title: "Vocabulary is not enough",
        body:
          "A strong explanation shows what causes what. Use your tests to connect the battery, closed circuit, motor, off-center mass, vibration, marker contact, robot motion, and artwork.",
        tone: "mystery",
      },
      {
        id: "tests-summary",
        type: "prior_work_summary",
        title: "Evidence from your two tests",
        prompt:
          "Use these observations as evidence, not decoration.",
        items: [
          {
            source_step_id: "first-art-test",
            field: "test1_record.pattern",
            label: "Test 1 pattern",
            value_labels: {
              tight_loops: "Tight loops",
              wide_loops: "Wide loops",
              zigzag: "Zigzags or wandering lines",
              uneven: "Uneven or incomplete marks",
              mixed: "Mixed pattern",
            },
          },
          {
            source_step_id: "engineering-improvement",
            field: "selected_design_variable",
            label: "Variable changed",
            value_labels: {
              marker_angle: "Marker angle",
              marker_height: "Marker height or paper contact",
              leg_spacing: "Marker leg spacing",
              weight_position: "Off-center weight position",
              motor_position: "Motor position or security",
              battery_balance: "Battery position and body balance",
              paper_surface: "Paper or testing surface",
              secure_wiring: "Electrical connection security",
            },
          },
          {
            source_step_id: "engineering-improvement",
            field: "test2_record.pattern",
            label: "Test 2 pattern",
            value_labels: {
              tight_loops: "Tight loops",
              wide_loops: "Wide loops",
              zigzag: "Zigzags or wandering lines",
              uneven: "Uneven or incomplete marks",
              mixed: "Mixed pattern",
            },
          },
          {
            source_step_id: "engineering-improvement",
            field: "redesign_conclusion",
            label: "Redesign conclusion",
            value_labels: {
              improved: "Improved for the stated goal",
              worse: "Performed worse for the stated goal",
              different_useful: "Different but useful",
              not_enough: "Not enough evidence yet",
            },
          },
          {
            source_step_id: "engineering-improvement",
            field: "test_comparison",
            label: "Evidence comparison",
          },
        ],
      },
      {
        id: "system-gallery",
        type: "image_gallery",
        title: "See the robot as one connected system",
        prompt:
          "The system map and final synthesis image show how invisible processes become visible artwork.",
        images: [
          {
            id: "journey-map",
            title: "From theory to working system",
            path: "drawing-robot/step-9/journey_theory_to_reality.png",
            alt:
              "A visual journey from scientific concepts through physical robot construction.",
          },
          {
            id: "system-synthesis",
            title: "The engineering art show",
            path: "drawing-robot/step-9/system_synthesis.png",
            alt:
              "A cutaway drawing robot connected to energy, circuit, vibration, balance, and friction evidence.",
          },
        ],
        save_as: "selected_system_evidence",
      },
      {
        id: "energy-motion-sequence",
        type: "sequence_builder",
        title: "Build the complete cause-and-effect chain",
        prompt:
          "Arrange the system from stored energy to the final drawing.",
        save_as: "final_system_sequence",
        items: [
          { id: "chemical", label: "Battery stores chemical energy" },
          { id: "closed_circuit", label: "A closed circuit allows current to flow" },
          { id: "motor_rotation", label: "The motor converts electrical energy into rotation" },
          { id: "offcenter_force", label: "The off-center mass creates a changing unbalanced force" },
          { id: "vibration", label: "The robot body vibrates" },
          { id: "contact_friction", label: "Marker contact and friction shape how the robot moves" },
          { id: "artwork", label: "Moving marker tips create visible patterns" },
        ],
        correct_order: [
          "chemical",
          "closed_circuit",
          "motor_rotation",
          "offcenter_force",
          "vibration",
          "contact_friction",
          "artwork",
        ],
        correct_feedback:
          "Yes. That chain connects energy, electricity, motion, forces, contact, and artwork.",
        retry_feedback:
          "Begin with where energy is stored, then follow the system one cause at a time.",
        required: true,
      },
      {
        id: "system-jobs",
        type: "matching",
        title: "Connect each subsystem to its job",
        prompt:
          "Choose the causal role—not merely the name—of each part.",
        save_as: "final_system_jobs",
        items: [
          {
            id: "battery",
            label: "Battery",
            options: [
              { id: "stores", label: "Stores chemical energy and provides electrical potential" },
              { id: "vibrates", label: "Directly vibrates the robot body" },
              { id: "draws", label: "Makes the marks on paper" },
            ],
          },
          {
            id: "closed_circuit",
            label: "Closed circuit",
            options: [
              { id: "path", label: "Provides a continuous path for current" },
              { id: "balances", label: "Balances the robot body" },
              { id: "colors", label: "Chooses the marker color" },
            ],
          },
          {
            id: "motor",
            label: "Motor",
            options: [
              { id: "rotation", label: "Converts electrical energy into rotation" },
              { id: "storage", label: "Stores chemical energy" },
              { id: "friction", label: "Creates all surface friction" },
            ],
          },
          {
            id: "offcenter",
            label: "Off-center mass",
            options: [
              { id: "imbalance", label: "Turns rotation into changing imbalance and vibration" },
              { id: "switch", label: "Opens and closes the electrical path" },
              { id: "paper", label: "Holds the paper still" },
            ],
          },
          {
            id: "markers",
            label: "Marker legs",
            options: [
              { id: "contact", label: "Support the body and transfer motion into marks through contact" },
              { id: "charge", label: "Recharge the battery" },
              { id: "insulate", label: "Prevent all motion" },
            ],
          },
        ],
      },
      {
        id: "imbalance-recap-video",
        type: "video_asset",
        title: "Optional Recap: The Imbalance Engine",
        mission:
          "Use this station when you need to strengthen the rotation-to-vibration part of your explanation.",
        asset: {
          id: "drawing-robot-imbalance-engine",
          type: "saras_video",
          asset_ref: {
            source_video_id: 7033,
            tag_id: 30050,
          },
          provenance: {
            source_type: "collection",
            collection_id: 159,
            collection_item_id: 136,
          },
          content_context: {
            base_board: "saras",
            language_id: 1,
            board_topic_id: 933,
            source_topic_id: 959,
          },
        },
      },
      {
        id: "circuit-recap-video",
        type: "video_asset",
        title: "Optional Recap: Electricity Needs a Complete Road",
        mission:
          "Use this station when you need to strengthen the battery-to-motor part of your explanation.",
        asset: {
          id: "drawing-robot-circuit-recap",
          type: "saras_video",
          asset_ref: {
            source_video_id: 7034,
            tag_id: 30051,
          },
          provenance: {
            source_type: "collection",
            collection_id: 159,
            collection_item_id: 136,
          },
          content_context: {
            base_board: "saras",
            language_id: 1,
            board_topic_id: 933,
            source_topic_id: 959,
          },
        },
      },
      {
        id: "circuit-defense",
        type: "quiz_question",
        title: "Defense check: electrical pathway",
        prompt:
          "The battery is installed, but the switch is open. Which statement is best?",
        choices: [
          { id: "motor_runs", label: "The motor runs because the battery contains energy" },
          { id: "no_current", label: "The motor does not run because the circuit path is incomplete" },
          { id: "more_vibration", label: "The open switch creates extra vibration" },
          { id: "markers_charge", label: "The markers provide current instead" },
        ],
        correct_answer: "no_current",
        save_as: "circuit_defense",
        correct_feedback:
          "Correct. Stored energy is available, but current needs a complete closed path.",
        incorrect_feedback:
          "Separate stored energy from the path required for current to flow.",
      },
      {
        id: "motion-defense",
        type: "quiz_question",
        title: "Defense check: vibration pathway",
        prompt:
          "Why does the off-center mass matter?",
        choices: [
          { id: "imbalance", label: "It creates a changing imbalance as it rotates" },
          { id: "battery", label: "It stores the battery's chemical energy" },
          { id: "switch", label: "It replaces the electrical switch" },
          { id: "color", label: "It selects the drawing color" },
        ],
        correct_answer: "imbalance",
        save_as: "motion_defense",
        correct_feedback:
          "Correct. The changing imbalance produces a repeating changing force and vibration.",
        incorrect_feedback:
          "Focus on what happens when a mass rotates away from the center of the shaft.",
      },
      {
        id: "final-science-explanation",
        type: "evidence_response",
        title: "Write the complete system explanation",
        prompt:
          "Explain how the robot works using circuit, motor, rotation, off-center mass, vibration, friction or contact, motion, and artwork.",
        placeholder:
          "The battery... When the circuit... The motor... Because the mass is off-center... The marker legs...",
        save_as: "final_science_explanation",
        min_length: 90,
        required: true,
      },
      {
        id: "evidence-for-explanation",
        type: "evidence_response",
        title: "Support the explanation with test evidence",
        prompt:
          "Use at least two observations from Test 1 or Test 2 that support your cause-and-effect explanation.",
        placeholder:
          "One observation was... This supports the explanation because... A second observation...",
        save_as: "science_evidence",
        min_length: 55,
        required: true,
      },
      {
        id: "step9-saras",
        type: "saras_checkpoint",
        title: "Practice the engineer's defense with Saras",
        suggested_questions: [
          "Does my explanation show cause and effect clearly?",
          "Where should friction or marker contact appear in my explanation?",
          "Which observations from my tests are strongest evidence?",
          "Can you ask me one engineer's defense question at a time?",
        ],
        hidden_context:
          "The student is constructing the final scientific explanation of the Drawing Robot. Use their Test 1, controlled redesign, Test 2, system sequence, and written explanation. Require causal links, not vocabulary dumping. Ask the student to connect claims to observations. Do not rewrite the explanation for them. Ask one defense question at a time and help them improve clarity while preserving their voice.",
        reveal_policy:
          "You may discuss the complete system. Help organize and test the student's reasoning rather than supplying a polished final answer.",
      },
      {
        id: "step9-completion",
        type: "completion_gate",
        incomplete_title: "Complete the engineer's defense",
        complete_title: "Mystery scientifically solved",
        incomplete_prompt:
          "Finish the system chain, causal roles, defense checks, explanation, and evidence.",
        complete_prompt:
          "You connected every subsystem and supported the explanation with observations from your robot.",
        rules: [
          {
            field: "final_system_sequence",
            condition: "sequence_correct",
          },
          { field: "final_system_jobs", condition: "completed" },
          {
            field: "circuit_defense",
            condition: "equals",
            value: "no_current",
          },
          {
            field: "motion_defense",
            condition: "equals",
            value: "imbalance",
          },
          {
            field: "final_science_explanation",
            condition: "min_length",
            value: 90,
          },
          {
            field: "science_evidence",
            condition: "min_length",
            value: 55,
          },
        ],
      },
    ],
  },

  "robot-art-gallery": {
    step_id: "robot-art-gallery",
    title: "Robot Art Gallery",
    subtitle: "Share, Defend, and Reflect",
    phase: "Share and Reflect",
    story:
      "The investigation ends with a public reveal. Present the robot as an engineered system, the artwork as physical evidence, and the redesign as a story of learning—not perfection.",
    blocks: [
      {
        id: "step10-story",
        type: "story_hook",
        eyebrow: "FINAL EXHIBIT",
        title: "You are the engineer and the artist",
        body:
          "Different robots can produce different successful results. Your task is to explain your design choices, show evidence, and identify the next question worth investigating.",
        tone: "mystery",
      },
      {
        id: "final-case-summary",
        type: "prior_work_summary",
        title: "Your engineering story",
        prompt:
          "Use this evidence when preparing the gallery presentation.",
        items: [
          {
            source_step_id: "engineering-improvement",
            field: "selected_design_variable",
            label: "Design variable changed",
            value_labels: {
              marker_angle: "Marker angle",
              marker_height: "Marker height or paper contact",
              leg_spacing: "Marker leg spacing",
              weight_position: "Off-center weight position",
              motor_position: "Motor position or security",
              battery_balance: "Battery position and body balance",
              paper_surface: "Paper or testing surface",
              secure_wiring: "Electrical connection security",
            },
          },
          {
            source_step_id: "engineering-improvement",
            field: "redesign_conclusion",
            label: "Redesign result",
            value_labels: {
              improved: "Improved for the stated goal",
              worse: "Performed worse for the stated goal",
              different_useful: "Different but useful",
              not_enough: "Not enough evidence yet",
            },
          },
          {
            source_step_id: "engineering-improvement",
            field: "test_comparison",
            label: "Test evidence",
          },
          {
            source_step_id: "explain-the-science",
            field: "final_science_explanation",
            label: "Scientific explanation",
          },
        ],
      },
      {
        id: "gallery-images",
        type: "image_gallery",
        title: "Prepare the final reveal",
        prompt:
          "These images celebrate the connected roles of scientist, engineer, tester, and artist.",
        images: [
          {
            id: "mission-accomplished",
            title: "Mission accomplished",
            path: "drawing-robot/step-10/mission_accomplished.png",
            alt:
              "A celebratory engineering workbench recognizing energy, electricity, motion, structure, and art.",
          },
          {
            id: "master-engineer",
            title: "Master full-stack engineer",
            path: "drawing-robot/step-10/master_full_stack_engineer.png",
            alt:
              "A completion screen celebrating stable structure, closed circuit, wobble control, and robot artwork.",
          },
        ],
        save_as: "selected_gallery_inspiration",
      },
      {
        id: "robot-name",
        type: "evidence_response",
        title: "Name your robot artist",
        prompt:
          "Choose a name that fits its motion, artwork, personality, or engineering story.",
        placeholder: "Our robot artist is called...",
        save_as: "robot_name",
        min_length: 2,
        required: true,
      },
      {
        id: "artwork-title",
        type: "evidence_response",
        title: "Title the gallery artwork",
        prompt:
          "Give the selected piece a title that connects to its pattern or motion.",
        placeholder: "The artwork is titled...",
        save_as: "artwork_title",
        min_length: 2,
        required: true,
      },
      {
        id: "gallery-piece",
        type: "choice_cards",
        title: "Select the gallery evidence",
        prompt:
          "Which result best tells your engineering story?",
        choices: [
          { id: "test1", label: "Test 1 artwork" },
          { id: "test2", label: "Test 2 artwork" },
          { id: "both", label: "Show both as a before-and-after comparison" },
          { id: "live_demo", label: "Use a live demonstration plus one artwork" },
        ],
        save_as: "gallery_piece",
        required: true,
      },
      {
        id: "gallery-presentation",
        type: "experiment_record",
        title: "Build the gallery presentation",
        prompt:
          "Use your own words. Saras may help organize, but should not replace your explanation.",
        save_as: "gallery_presentation",
        fields: [
          {
            id: "challenge",
            type: "text",
            label: "What mystery or engineering challenge did you investigate?",
            placeholder: "We investigated...",
            min_length: 15,
          },
          {
            id: "science",
            type: "text",
            label: "How does the robot work?",
            placeholder: "The battery and circuit... The motor and off-center mass...",
            min_length: 35,
          },
          {
            id: "change",
            type: "text",
            label: "What one design change did you test?",
            placeholder: "We changed...",
            min_length: 15,
          },
          {
            id: "evidence",
            type: "text",
            label: "What evidence supports your conclusion?",
            placeholder: "The evidence was...",
            min_length: 25,
          },
          {
            id: "proud",
            type: "text",
            label: "What are you most proud of?",
            placeholder: "I am proud that...",
            min_length: 12,
          },
          {
            id: "next",
            type: "text",
            label: "What would you investigate next?",
            placeholder: "My next question would be...",
            min_length: 12,
          },
        ],
      },
      {
        id: "engineers-defense",
        type: "quiz_question",
        title: "Engineer's defense challenge",
        prompt:
          "Which statement is the strongest engineering claim?",
        choices: [
          {
            id: "pretty",
            label: "Our robot is best because its picture looks the prettiest",
          },
          {
            id: "evidence",
            label:
              "Changing one variable altered the motion and pattern, as shown by our two test records",
          },
          {
            id: "worked",
            label: "It worked because we were lucky",
          },
          {
            id: "all_changes",
            label: "We changed many things, so we know which one caused the result",
          },
        ],
        correct_answer: "evidence",
        save_as: "engineers_defense",
        correct_feedback:
          "Correct. A strong engineering claim identifies the change and supports it with controlled-test evidence.",
        incorrect_feedback:
          "Look for the claim that connects one controlled change to recorded evidence.",
      },
      {
        id: "completion-pwo",
        type: "pwo_asset",
        title: "Complete the Virtual Drawing Robot Journey",
        mission:
          "Open the final scene and compare its summary with the explanation you created from your own evidence.",
        save_as: "completion_pwo_opened",
        asset: {
          id: "drawing-robot-i-learned",
          type: "saras_pwo",
          asset_ref: {
            source_pwo_id: 1168,
            scene_name: "i learned",
          },
          provenance: {
            source_type: "collection",
            collection_id: 159,
            collection_item_id: 136,
          },
          content_context: {
            base_board: "saras",
            language_id: 1,
            board_topic_id: 933,
            source_topic_id: 959,
          },
        },
      },
      {
        id: "final-reflection",
        type: "evidence_response",
        title: "Final reflection",
        prompt:
          "What changed in your thinking from the first mysterious artwork to the final robot? Include one new question you now have.",
        placeholder:
          "At first I thought... Now I understand... A new question I have is...",
        save_as: "final_reflection",
        min_length: 45,
        required: true,
      },
      {
        id: "step10-saras",
        type: "saras_checkpoint",
        title: "Rehearse the gallery defense with Saras",
        suggested_questions: [
          "Ask me one presentation question at a time.",
          "Does my claim use evidence from both tests?",
          "Where is the cause-and-effect connection unclear?",
          "Help me organize my ideas without rewriting them.",
        ],
        hidden_context:
          "The student is preparing the final Drawing Robot gallery presentation. Use their redesign evidence and scientific explanation. Ask one question at a time. Request visible or recorded evidence for claims. Help organize the student's voice but do not write the presentation for them. Celebrate unexpected results and different successful solutions. Encourage a next investigable question.",
        reveal_policy:
          "The entire system may now be discussed. Preserve authorship by coaching rather than replacing the student's words.",
      },
      {
        id: "step10-completion",
        type: "completion_gate",
        incomplete_title: "Prepare the final gallery defense",
        complete_title: "Journey complete: engineer and artist",
        incomplete_prompt:
          "Name the robot and artwork, prepare the presentation, answer the defense challenge, open the completion scene, and reflect.",
        complete_prompt:
          "You solved the mystery, built and improved a working system, defended the science with evidence, and shared the result.",
        rules: [
          { field: "robot_name", condition: "min_length", value: 2 },
          { field: "artwork_title", condition: "min_length", value: 2 },
          { field: "gallery_piece", condition: "answered" },
          {
            field: "gallery_presentation",
            condition: "record_complete",
          },
          {
            field: "engineers_defense",
            condition: "equals",
            value: "evidence",
          },
          { field: "completion_pwo_opened", condition: "truthy" },
          {
            field: "final_reflection",
            condition: "min_length",
            value: 45,
          },
        ],
      },
      {
        id: "after-camp-robot-artist-mission",
        type: "story_hook",
        eyebrow: "AFTER-CAMP INDEPENDENT MISSION",
        title: "Open Your Independent Robot Art Studio",
        body:
          "Set aside about 60–90 minutes later today. Work independently to create a three-artwork mini-series or improve one engineering goal. Plan one controlled change, switch the power off before every adjustment, protect the testing surface, and use evidence from three art trials.",
        tone: "mystery",
        instructional_mode: "you_do",
        student_cue:
          "Complete this mission independently after the live camp session. Your guided Journey is already complete; this is your next artist-engineer challenge.",
      },
      {
        id: "after-camp-robot-artist-choice",
        type: "choice_cards",
        title: "Choose your independent studio challenge",
        prompt:
          "Choose one goal that combines creative intention with a fair engineering test. You may do more than one if time allows.",
        choices: [
          {
            id: "pattern_family",
            label:
              "Pattern-family challenge — create three related artworks using one controlled design change",
          },
          {
            id: "stable_artist",
            label:
              "Stable-artist challenge — improve upright motion and produce a more consistent pattern",
          },
          {
            id: "marker_arrangement",
            label:
              "Marker-arrangement challenge — investigate marker spacing, angle, or paper contact",
          },
          {
            id: "color_layers",
            label:
              "Color-layer challenge — create intentional color relationships while keeping the mechanical test controlled",
          },
        ],
        save_as: "after_camp_robot_artist_choice",
        required: false,
        instructional_mode: "you_do",
        student_cue:
          "Select the challenge you will own from planning through artwork evidence and explanation.",
      },
      {
        id: "after-camp-robot-artist-plan",
        type: "experiment_record",
        title: "Write your artist-engineer plan",
        prompt:
          "Choose one main variable, decide what will stay the same, and predict how the marks may change before adjusting the robot.",
        save_as: "after_camp_robot_artist_plan",
        required: false,
        instructional_mode: "you_do",
        student_cue:
          "Complete the plan in your own words before touching the robot or markers.",
        fields: [
          {
            id: "artistic_goal",
            label: "My artistic goal",
            type: "text",
            placeholder: "I want the three artworks to show...",
            min_length: 20,
          },
          {
            id: "one_variable",
            label: "The one main engineering variable I will change",
            type: "text",
            placeholder: "I will change...",
            min_length: 20,
          },
          {
            id: "keep_same",
            label: "What I will keep the same for a fair comparison",
            type: "text",
            placeholder: "I will keep... the same because...",
            min_length: 25,
          },
          {
            id: "prediction",
            label: "My prediction about the robot motion and artwork",
            type: "text",
            placeholder: "I predict the robot will... and the marks will... because...",
            min_length: 35,
          },
          {
            id: "success_evidence",
            label: "What evidence will show that the challenge succeeded?",
            type: "text",
            placeholder: "I will look for evidence such as...",
            min_length: 30,
          },
        ],
      },
      {
        id: "after-camp-robot-artist-safety",
        type: "multi_select",
        title: "Complete the independent studio safety check",
        prompt:
          "Check each statement only when you are ready to follow it during every trial.",
        choices: [
          {
            id: "power_off",
            label:
              "I will switch the robot OFF before touching, lifting, or adjusting any part",
          },
          {
            id: "clear_rotation",
            label:
              "I will keep fingers, hair, loose clothing, and objects away from rotating parts",
          },
          {
            id: "secure_parts",
            label:
              "I will check that the battery holder, motor, wires, markers, and supports are secure before each test",
          },
          {
            id: "protect_surface",
            label:
              "I will protect the table or floor and test only on teacher- or family-approved paper",
          },
          {
            id: "approved_adjustments",
            label:
              "I will adjust only teacher-approved parts and will not force, cut, or add unapproved materials",
          },
        ],
        save_as: "after_camp_robot_artist_safety",
        min_selections: 5,
        required: false,
        instructional_mode: "you_do",
        student_cue:
          "Take responsibility for safe studio testing before you begin the three artworks.",
      },
      {
        id: "after-camp-robot-artist-trials",
        type: "experiment_record",
        title: "Create and record three independent artworks",
        prompt:
          "Use the same observation categories for all three trials: loops, crossings, spacing, smoothness, repetition, travel direction, stability, and unexpected behavior.",
        save_as: "after_camp_robot_artist_trials",
        required: false,
        instructional_mode: "you_do",
        student_cue:
          "Create three artworks, record what actually happened, and make only power-off adjustments between trials.",
        fields: [
          {
            id: "artwork_one",
            label: "Artwork 1 — setup, robot motion, and visible pattern evidence",
            type: "text",
            placeholder:
              "For Artwork 1, the setup was... The robot moved... The pattern showed...",
            min_length: 40,
          },
          {
            id: "between_one_two",
            label:
              "Power-off adjustment before Artwork 2, or why no adjustment was needed",
            type: "text",
            placeholder:
              "Before Artwork 2, I switched off the robot and...",
            min_length: 25,
          },
          {
            id: "artwork_two",
            label: "Artwork 2 — setup, robot motion, and visible pattern evidence",
            type: "text",
            placeholder:
              "For Artwork 2, the setup was... The robot moved... The pattern showed...",
            min_length: 40,
          },
          {
            id: "between_two_three",
            label:
              "Power-off adjustment before Artwork 3, or why no adjustment was needed",
            type: "text",
            placeholder:
              "Before Artwork 3, I switched off the robot and...",
            min_length: 25,
          },
          {
            id: "artwork_three",
            label: "Artwork 3 — setup, robot motion, and visible pattern evidence",
            type: "text",
            placeholder:
              "For Artwork 3, the setup was... The robot moved... The pattern showed...",
            min_length: 40,
          },
          {
            id: "series_comparison",
            label: "What changed and what stayed consistent across the series?",
            type: "text",
            placeholder: "Across the three artworks, I noticed...",
            min_length: 40,
          },
        ],
      },
      {
        id: "after-camp-robot-artist-conclusion",
        type: "evidence_response",
        title: "Write your independent artist-engineer statement",
        prompt:
          "Explain how the chosen variable affected the robot motion and artwork. Use evidence from all three trials and connect your result to energy, circuit, rotation, off-center motion, balance, marker contact, or friction.",
        placeholder:
          "My mini-series investigated... Across the three artworks... The strongest evidence was... The engineering reason is... My next studio test would be...",
        save_as: "after_camp_robot_artist_conclusion",
        min_length: 100,
        required: false,
        instructional_mode: "you_do",
        student_cue:
          "Use your own artwork evidence to explain both the creative result and the engineering cause.",
      },
      {
        id: "after-camp-robot-artist-saras",
        type: "saras_checkpoint",
        title: "Challenge your mini-gallery explanation with Saras",
        suggested_questions: [
          "Ask me to defend my statement using all three artworks.",
          "Did I really change only one main engineering variable?",
          "Which visible pattern evidence best supports my claim?",
          "What would be the fairest next studio test?",
        ],
        hidden_context:
          "The student is completing the optional after-camp Robot Artist Studio mission after the guided ten-step Journey. Use after_camp_robot_artist_choice, after_camp_robot_artist_plan, after_camp_robot_artist_safety, after_camp_robot_artist_trials, and after_camp_robot_artist_conclusion. Ask for visible evidence from all three artworks. Check whether the student changed one main variable and kept other conditions reasonably consistent. Reinforce switching power off before adjustments, keeping clear of rotating parts, securing components, protecting the testing surface, and using only approved adjustments and materials. Coach the student's reasoning and gallery explanation without rewriting it.",
        reveal_policy:
          "The full Drawing Robot science may be discussed. Emphasize controlled testing, safe power-off adjustments, visible evidence, creative intention, and honest limits on the conclusion.",
        instructional_mode: "you_do",
        student_cue:
          "Use Saras as a coach after you have written your own artist-engineer statement.",
      },
    ],
  },
};

export const getDrawingRobotJourneyPage = (stepId) =>
  DRAWING_ROBOT_STEP_PAGES[String(stepId || "")] || null;

export default DRAWING_ROBOT_STEP_PAGES;
